import { rpcClient } from './rpcClient';

/**
 * RPC client for leads-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcLeads = {
  /**
   * List leads with filters
   */
  list(payload: {
    q?: string;
    status?: string;
    statuses?: string[];
    rating?: string;
    managerId?: string;
    agentId?: string;
    leadNum?: string;
    firstName?: string;
    source?: string;
    page?: number;
    limit?: number;
    isArchived?: boolean; // Admin only: false = exclude archived, true = only archived, omit = show all
    convertedOnly?: boolean; // true = only leads with convertedAt set (for count)
    phoneExact?: string; // For import: find by normalized phone (handles 050-123-4567 vs 0501234567)
    emailExact?: string; // For import: find by normalized email (lowercase)
  }) {
    return rpcClient.post('leads', '/list', payload);
  },

  /**
   * Create new lead
   */
  create(payload: {
    fullName: string;
    phone: string;
    email: string;
    business: string;
    source?: string;
    area?: string;
    notes?: string;
    agreeMarketing?: boolean;
    rating?: 'HOT' | 'WARM' | 'COLD';
    callHours?: string;
    city?: string;
    adName?: string;
    managerIds?: string[];
    agentIds?: string[];
  }) {
    return rpcClient.post('leads', '/create', payload);
  },

  /**
   * Update lead
   */
  update(leadId: string, updateData: {
    fullName?: string;
    phone?: string;
    email?: string;
    business?: string;
    status?: string;
    rating?: string;
    source?: string;
    area?: string;
    notes?: string;
    callHours?: string;
    city?: string;
    adName?: string;
    agreeMarketing?: boolean;
    managerIds?: string[];
    agentIds?: string[];
  }) {
    // CRITICAL: Optimized timeout for leads update (8 seconds - faster than default 10s)
    return rpcClient.post('leads', '/update', { leadId, ...updateData }, {
      timeout: 8000,
    });
  },

  /**
   * Assign managers to lead
   */
  assignManagers(leadId: string, managerIds: string[]) {
    return rpcClient.post('leads', '/assign-managers', { leadId, managerIds });
  },

  /**
   * Archive or unarchive leads (admin only)
   */
  archive(leadIds: string | string[], isArchived: boolean) {
    const ids = Array.isArray(leadIds) ? leadIds : [leadIds];
    return rpcClient.post('leads', '/archive', { leadIds: ids, isArchived });
  },

  /**
   * Assign agents to lead
   */
  assignAgents(leadId: string, agentIds: string[]) {
    return rpcClient.post('leads', '/assign-agents', { leadId, agentIds });
  },

  /**
   * Convert lead to client
   */
  convert(leadId: string) {
    return rpcClient.post('leads', '/convert', { leadId });
  },

  /**
   * Get lead statuses
   */
  getStatuses() {
    return rpcClient.post('leads', '/statuses/list', {});
  },

  /**
   * Update lead statuses
   */
  updateStatuses(statuses: Array<{ key: string; name: string; order: number; active: boolean }>) {
    return rpcClient.post('leads', '/statuses/update', { statuses });
  },

  /**
   * Get notes for a lead
   */
  getNotes(leadId: string) {
    return rpcClient.post('leads', '/notes/list', { leadId });
  },

  /**
   * Create a note for a lead
   */
  createNote(leadId: string, text: string) {
    return rpcClient.post('leads', '/notes/create', { leadId, text });
  },

  /**
   * Update a note for a lead
   */
  updateNote(noteId: string, text: string) {
    return rpcClient.post('leads', '/notes/update', { noteId, text });
  },

  /**
   * Get managers for lead assignment
   */
  getManagers(payload?: { q?: string; limit?: number }) {
    return rpcClient.post('leads', '/managers/list', payload || {});
  },

  /**
   * Notify lead managers
   */
  notifyManagers(leadId: string, managerIds: string[]) {
    return rpcClient.post('leads', '/notify-managers', { leadId, managerIds });
  },

  /**
   * List attachments for a lead
   */
  listAttachments(leadId: string) {
    return rpcClient.post('leads', '/attachments/list', { leadId });
  },

  /**
   * Upload attachments for a lead
   */
  uploadAttachments(leadId: string, files: FileList | File[]) {
    const formData = new FormData();
    formData.append('leadId', leadId);
    Array.from(files).forEach(f => formData.append('files', f));
    return rpcClient.post('leads', '/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 seconds - file uploads can take time, especially for large files
    });
  },

  /**
   * Delete an attachment
   */
  deleteAttachment(leadId: string, attId: string) {
    return rpcClient.post('leads', '/attachments/delete', { leadId, attId });
  },

  /**
   * Create a public lead (from landing pages/campaigns)
   */
  createPublic(payload: {
    fullName: string;
    phone: string;
    email: string;
    business: string;
    source?: string;
    area?: string;
    agreeMarketing?: boolean;
    rating?: 'HOT' | 'WARM' | 'COLD';
    landingId?: string;
    landingName?: string;
  }) {
    return rpcClient.post('leads', '/public/create', payload);
  },

  /**
   * Upload leads import file
   */
  uploadLeadsImport(formData: FormData, lang: string = 'he') {
    return rpcClient.post('leads', '/upload-leads-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { lang },
      timeout: 60000, // 60 seconds for file upload
    });
  },

  /**
   * Run leads import dry run
   */
  runLeadsImportDryRun(uploadId: string, mapping: any, options: any) {
    return rpcClient.post('leads', '/run-leads-import-dry-run', {
      uploadId,
      mapping,
      options,
    });
  },

  /**
   * Commit leads import (pass mapping and options so commit works even if dry-run did not persist them)
   */
  commitLeadsImport(uploadId: string, mapping?: Record<string, string | null>, options?: Record<string, any>) {
    return rpcClient.post('leads', '/commit-leads-import', { uploadId, mapping, options }, {
      timeout: 300000, // 5 minutes for large imports
    });
  },
};

export default rpcLeads;

